import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getEnrichmentApplierName } from 'hooks/useGruAgent/helpers';
import { ActionResultWithIds, SuccessfulEnrichment } from 'hooks/useGruAgent/types';
import { closeAgentSuggestionModal, openAgentSuggestionModal } from 'stores/agentSuggestion/agentSuggestion';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { TransactionsRoutes } from 'support/types';

import { EnrichDocumentAgentCard } from '../EnrichDocumentAgentCard/EnrichDocumentAgentCard';

type AgentSuggestionContentProps = {
  actionResults: Array<ActionResultWithIds>;
  agentProposal: SuccessfulEnrichment;
  canonical: Record<string, unknown> | undefined | null;
  messageId: string;
};

const AgentSuggestionContent = ({
  actionResults,
  agentProposal,
  canonical,
  messageId,
}: AgentSuggestionContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const uiConfig = useWebEDIStore((state) => state.uiConfig);

  const isApplied = actionResults.some((ar) => ar.action === 'apply_enrichment' && ar.status === 'approved');
  const applierName = getEnrichmentApplierName(actionResults);

  const handlePreview = useCallback(
    (jqExpression: string) => {
      openAgentSuggestionModal(jqExpression, agentProposal);
    },
    [agentProposal],
  );

  const handleApply = useCallback(() => {
    // dismiss any open preview modal before navigating away
    closeAgentSuggestionModal();
    navigate(routeToPage(TransactionsRoutes.editSuggestion, { id: messageId }));
  }, [messageId, navigate]);

  // t('webedi:sidebar.agents.appliedIntro')
  // t('webedi:sidebar.agents.appliedIntroGeneric')
  const introText = isApplied
    ? applierName
      ? t('webedi:sidebar.agents.appliedIntro', { userName: applierName })
      : t('webedi:sidebar.agents.appliedIntroGeneric')
    : t('webedi:sidebar.agents.intro');

  return (
    <>
      <p className="text-sm leading-5 font-normal text-gray-900">{introText}</p>
      <EnrichDocumentAgentCard
        agentProposal={agentProposal}
        isApplied={isApplied}
        uiConfig={uiConfig}
        canonical={canonical}
        onPreview={handlePreview}
        onApply={handleApply}
      />
    </>
  );
};

export { AgentSuggestionContent };
